import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, Receipt } from "lucide-react";

import {
    getPaymentsAvailability,
    listPortalPayments,
} from "../../application/portalServices";
import type { PortalPayment, PortalPaymentStatus } from "../../domain/types";

const STATUS_CLASSES: Record<PortalPaymentStatus, string> = {
    pending: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    paid: "bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300",
    cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

/** Format a decimal string as euros. Never accumulates in floating point. */
const formatMoney = (amount: string): string => {
    if (amount.trim() === "") return "—";
    const value = Number(amount);
    if (!Number.isFinite(value)) return "—";
    return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
    }).format(value);
};

export default function PortalPaymentsPage() {
    const { t } = useTranslation();
    const [payments, setPayments] = useState<PortalPayment[]>([]);
    const [enabled, setEnabled] = useState<boolean | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        // Availability first: a therapist who does not use the tool should see
        // an explanation, not an empty list that reads like a bug.
        getPaymentsAvailability()
            .then((data) => {
                if (cancelled) return;
                setEnabled(data.payments_enabled);
                if (!data.payments_enabled) {
                    setLoading(false);
                    return;
                }
                return listPortalPayments().then((list) => {
                    if (cancelled) return;
                    setPayments(list);
                    setLoading(false);
                });
            })
            .catch(() => {
                if (cancelled) return;
                setError(true);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-content-primary">
                    {t("portal.payments.title")}
                </h1>
                <p className="mt-1 text-sm text-content-tertiary">
                    {t("portal.payments.subtitle")}
                </p>
            </div>

            {loading && (
                <p className="text-sm text-gray-500">{t("common.loading")}</p>
            )}

            {!loading && error && (
                <p role="alert" className="text-sm text-red-600">
                    {t("portal.payments.loadError")}
                </p>
            )}

            {!loading && !error && enabled === false && (
                <EmptyCard message={t("portal.payments.notEnabled")} />
            )}

            {!loading && !error && enabled && payments.length === 0 && (
                <EmptyCard message={t("portal.payments.empty")} />
            )}

            {!loading && !error && enabled && payments.length > 0 && (
                <ul className="space-y-4">
                    {payments.map((payment) => (
                        <li
                            key={payment.id}
                            className="glass-effect glass-border rounded-2xl border p-5 shadow-sm"
                        >
                            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-content-primary">
                                            {payment.line_items[0]?.description ?? "—"}
                                        </span>
                                        <span
                                            role="status"
                                            className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_CLASSES[payment.status]}`}
                                        >
                                            {t(`portal.payments.status.${payment.status}`)}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-xs text-content-tertiary">
                                        {payment.sent_at ?? ""}
                                    </p>
                                </div>
                                <span className="text-lg font-semibold text-content-primary">
                                    {formatMoney(payment.total)}
                                </span>
                            </div>

                            <ul className="mb-4 space-y-1 text-sm text-gray-600 dark:text-gray-300">
                                {payment.line_items.map((item) => (
                                    <li key={item.id} className="flex justify-between gap-4">
                                        <span>{item.description}</span>
                                        <span className="shrink-0 text-gray-500">
                                            {formatMoney(item.subtotal)}
                                        </span>
                                    </li>
                                ))}
                            </ul>

                            <div className="flex flex-wrap items-center gap-3">
                                {/* The server withholds the link once settled, so
                                    this cannot invite a second payment. */}
                                {payment.payment_link_url && (
                                    <a
                                        href={payment.payment_link_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-brand-500 px-4 text-sm font-medium text-white hover:bg-brand-600"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        {t("portal.payments.payNow")}
                                    </a>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}

const EmptyCard: React.FC<{ message: string }> = ({ message }) => (
    <div className="glass-effect glass-border rounded-2xl border p-10 text-center shadow-sm">
        <Receipt className="mx-auto mb-3 h-8 w-8 text-gray-300 dark:text-gray-600" />
        <p className="text-sm text-content-tertiary">{message}</p>
    </div>
);
